import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';

import {ArrowsOut, X} from '../../icons';

/**
 * La animación de una tarea, con «Ampliar», y su vista ampliada.
 *
 * Es una COPIA de `StepMedia` y `MediaZoom` de `onboarding/drawer/SetupDrawer.js`, no un import: el
 * drawer viejo se borra en una card de después (contrato §1) y no se toca mientras tanto. Lo que
 * cambia respecto de allá: la vista ampliada maneja su propio Escape (en el drawer lo manejaba el
 * drawer, para no cerrar los dos a la vez) y se portalea al `<body>`, porque la página vive dentro de
 * un contenedor con `container-type` y un `fixed` adentro se posicionaría contra él.
 */

// Tamaño nativo de las animaciones del bundle (`src/images/onboarding/`): reserva el alto antes de
// que carguen. Las del Centro de Ayuda pueden tener otra proporción: el CSS las contiene.
const MEDIA_WIDTH = 1120;
const MEDIA_HEIGHT = 630;

/** Quien pidió menos movimiento ve el cuadro final quieto, no un loop que arranca solo. */
export function prefersReducedMotion() {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Sin animación no se dibuja NADA (un recuadro vacío se ve y no significa nada), y si el archivo no
 * carga el recuadro desaparece en vez de mostrar una imagen rota. Tocar la imagen también amplía: en
 * el teléfono «Ampliar» es un blanco chico.
 */
export function StepMedia({src, poster, title}) {
    const {t} = useTranslation();
    const [failed, setFailed] = useState(false);
    const [zoomed, setZoomed] = useState(false);
    const expandRef = useRef(null);
    const wasZoomed = useRef(false);

    // Al cerrar la vista ampliada el foco vuelve al «Ampliar» que la abrió, no al `<body>`.
    useEffect(() => {
        if (wasZoomed.current && !zoomed && expandRef.current) expandRef.current.focus();
        wasZoomed.current = zoomed;
    }, [zoomed]);

    if (!src || failed) return null;
    const shown = poster && prefersReducedMotion() ? poster : src;
    const alt = t('learn_dashbo.media.alt', {task: title, interpolation: {escapeValue: false}});

    return (
        <div className="ld-media">
            <img src={shown} alt={alt} width={MEDIA_WIDTH} height={MEDIA_HEIGHT}
                 onError={() => setFailed(true)} onClick={() => setZoomed(true)}/>
            <button type="button" className="ld-media__expand" ref={expandRef} onClick={() => setZoomed(true)}>
                <ArrowsOut size={13}/>
                {t('learn_dashbo.task.media_expand')}
            </button>
            {zoomed && <MediaZoom src={shown} alt={alt} onClose={() => setZoomed(false)}/>}
        </div>
    );
}

/**
 * La animación a tamaño de pantalla. Se cierra con su botón, con un clic afuera de la imagen o con
 * Escape.
 *
 * El Tab se atrapa desde el `document` y no desde el contenedor: un clic en la imagen dejaba el foco
 * en `<body>`, el listener del contenedor ya no se enteraba y el Tab recorría la app de atrás del
 * velo. Por lo mismo, la imagen no toma el foco al hacerle clic.
 */
export function MediaZoom({src, alt, onClose}) {
    const {t} = useTranslation();
    const closeRef = useRef(null);

    useEffect(() => {
        if (closeRef.current) closeRef.current.focus();
    }, []);

    // El cerrar es el único control: el Tab (y Shift+Tab) vuelve a él desde donde esté el foco.
    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
                return;
            }
            if (event.key !== 'Tab') return;
            event.preventDefault();
            if (closeRef.current) closeRef.current.focus();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    return createPortal(
        <div className="ld-zoom" role="dialog" aria-modal="true" aria-label={alt} onClick={onClose}>
            <button type="button" className="ld-zoom__close" ref={closeRef} onClick={onClose}
                    aria-label={t('learn_dashbo.media.close')}>
                <X size={18}/>
            </button>
            <img src={src} alt={alt} width={MEDIA_WIDTH} height={MEDIA_HEIGHT}
                 onMouseDown={(event) => event.preventDefault()}
                 onClick={(event) => event.stopPropagation()}/>
        </div>,
        document.body);
}
